/**
 * REPORT CATALOG — the single source of truth for how reports are
 * classified, described and searched.
 *
 * ## Why this file exists
 *
 * Classification used to be spread across five layers that each
 * computed the same value and fought each other:
 *
 *   1. `inferGroupFromName()`      — 30 keyword guesses off the report name
 *   2. `GROUP_NAME_NORMALIZE`      — renamed DB group variants
 *   3. `STATIC_REPORTS` overlay    — by pKey, then by lowercased name
 *   4. `PKEY_GROUP_OVERRIDES`      — ~100 hand-maintained pKey -> group entries
 *   5. config-derived re-inference — re-ran (1) against the config's name
 *
 * `nameForList` was recomputed three times as those layers landed, and
 * the catalog filtered on `CATEGORY_CONFIG[groupName] != null` — which
 * only ever matched entries produced by layer 4. In other words the
 * visible catalog *was* `PKEY_GROUP_OVERRIDES`, and any report nobody
 * had typed into that map silently disappeared. 35 reports with fully
 * working server-side generators were unreachable that way.
 *
 * This file replaces all five layers with one lookup.
 *
 * ## Two rules
 *
 * 1. **Keyed by slug, not pKey.** pKeys differ between dev/QA/prod
 *    databases, which is exactly why the old map needed constant
 *    hand-patching. Slugs are ours and stable.
 *
 * 2. **Fail open.** A report missing from `REPORT_CATALOG` still
 *    renders — it lands in the `unsorted` group instead of vanishing.
 *    `reportCatalog.coverage.test.ts` fails the build when that
 *    happens, so an unclassified report is a red CI run rather than a
 *    report nobody can find for two years.
 *
 * ## Adding a report
 *
 * Add one entry keyed by the config's slug. `description` shows on the
 * card and is searched; `keywords` are searched but never displayed —
 * put synonyms, acronyms and the words users actually say there
 * ("shopping list" for Grocery List, "I&O" for Intake Study).
 */

// ─────────────────────────────────────────────────────────────────
// GROUPS
// ─────────────────────────────────────────────────────────────────

/**
 * Top-level groups, ordered by when in the day someone reaches for
 * them rather than by which table the report reads.
 *
 * The previous five sections mixed four different kinds of thing —
 * a business function (Production), a data subject (Patient), an
 * audience (Employees) and a residual bucket (Facility, which held
 * 17 categories and 30 unrelated reports). That made classification a
 * judgment call every time, which is why it got skipped.
 */
export const CATALOG_GROUPS = {
  'meal-service': {
    label: 'Meal Service',
    blurb: 'Printed to run today’s meals',
    icon: 'bi-clipboard-check',
    color: '#ea580c',
    bgColor: '#ffedd5',
    order: 1,
  },
  clinical: {
    label: 'Clinical',
    blurb: 'Resident nutrition care and assessment',
    icon: 'bi-heart-pulse',
    color: '#7c3aed',
    bgColor: '#ede9fe',
    order: 2,
  },
  kitchen: {
    label: 'Kitchen & Purchasing',
    blurb: 'Recipes, ordering and production',
    icon: 'bi-basket',
    color: '#0891b2',
    bgColor: '#cffafe',
    order: 3,
  },
  census: {
    label: 'Census & Rosters',
    blurb: 'Who is here and what they are on',
    icon: 'bi-people',
    color: '#2563eb',
    bgColor: '#dbeafe',
    order: 4,
  },
  'selective-menus': {
    label: 'Selective Menus',
    blurb: 'Menu selection and returns',
    icon: 'bi-hand-index',
    color: '#c026d3',
    bgColor: '#fae8ff',
    order: 5,
  },
  financial: {
    label: 'Cost & Credits',
    blurb: 'Food cost, charges and credit balances',
    icon: 'bi-cash-stack',
    color: '#059669',
    bgColor: '#d1fae5',
    order: 6,
  },
  interfaces: {
    label: 'Interfaces & Menu Config',
    blurb: 'EHR feeds, AIM and global menu settings',
    icon: 'bi-hospital',
    color: '#0284c7',
    bgColor: '#e0f2fe',
    order: 7,
  },
  admin: {
    label: 'Staff & Admin',
    blurb: 'Employees, users and facility setup',
    icon: 'bi-person-badge',
    color: '#64748b',
    bgColor: '#f1f5f9',
    order: 8,
  },
  /**
   * Fail-open destination. Nothing should live here — the coverage
   * test fails the build if anything does. It exists so that an
   * unclassified report is visible and searchable while it waits to
   * be filed, rather than silently dropping out of the catalog.
   */
  unsorted: {
    label: 'Unsorted',
    blurb: 'Not yet filed, see reportCatalog.ts',
    icon: 'bi-question-circle',
    color: '#b45309',
    bgColor: '#fef3c7',
    order: 99,
  },
} as const;

export type CatalogGroupKey = keyof typeof CATALOG_GROUPS;

export const CATALOG_GROUP_ORDER: CatalogGroupKey[] = (
  Object.keys(CATALOG_GROUPS) as CatalogGroupKey[]
).sort((a, b) => CATALOG_GROUPS[a].order - CATALOG_GROUPS[b].order);

// ─────────────────────────────────────────────────────────────────
// ENTRIES
// ─────────────────────────────────────────────────────────────────

export interface CatalogEntry {
  group: CatalogGroupKey;
  /** One line, shown under the card title and included in search. */
  description: string;
  /**
   * Card title override. Only needed when the config's `reportName`
   * carries a qualifier that made sense in the flat WinForms list but
   * is noise here — "Production - Grocery List" sitting under Kitchen
   * & Purchasing, for example. Leave unset and the config name is used
   * with any redundant family prefix stripped, which covers most
   * reports. Search still matches the full underlying name.
   */
  title?: string;
  /**
   * Extra search terms — never displayed. Synonyms, acronyms, and the
   * words staff actually use. This is what makes search find a report
   * when the user does not know its official name.
   */
  keywords?: string;
  /**
   * Facility-token label to show as the card's small tag, when the
   * report belongs to a recognisable family (Beverage, Tray Line,
   * Weight, …). Purely cosmetic grouping within a group.
   */
  family?: string;
}

export const REPORT_CATALOG: Record<string, CatalogEntry> = {
  // ═══ MEAL SERVICE ════════════════════════════════════════════════
  'tray-tickets': {
    group: 'meal-service', family: 'Tray Tickets',
    description: 'Meal tray tickets for a service period',
    keywords: 'tray ticket card slip meal card diet card service',
  },
  'tray-ticket-notes': {
    group: 'meal-service', family: 'Tray Tickets',
    description: 'Notes printed alongside tray tickets',
    keywords: 'tray ticket note comment annotation',
  },
  'tray-tickets-v2': {
    group: 'meal-service', family: 'Tray Tickets',
    description: 'Alternate tray ticket layout',
    keywords: 'tray ticket variant alternate v2',
  },
  // Sorts with Tray Tickets rather than on its own: it is the same ticket,
  // reprinted from what was saved when it first printed.
  'past-tray-tickets': {
    group: 'meal-service', family: 'Tray Tickets',
    description: 'Reprints a tray ticket exactly as it printed on a past date',
    keywords: 'past previous prior reprint history tray ticket complaint survey',
  },
  'dish-up-labels': {
    group: 'meal-service', family: 'Labels',
    description: 'Labels applied at dish-up on the tray line',
    keywords: 'dish up label sticker plating steam table',
  },
  // Sorts with Labels rather than on its own, for the same reason Past Tray
  // Tickets sorts with Tray Tickets: it is the same label, reprinted from what
  // was saved when it first printed.
  'past-nourishment-labels': {
    group: 'meal-service', family: 'Labels',
    description: 'Reprints nourishment labels exactly as they printed on a past date',
    keywords: 'past previous prior reprint history nourishment label snack complaint survey',
  },
  'posting-menu-daily': {
    group: 'meal-service', family: 'Posting Menu',
    description: 'Daily menu posted for staff and residents',
    keywords: 'posting post menu board daily today',
  },
  'posting-menu-daily-full-day': {
    group: 'meal-service', family: 'Posting Menu',
    description: 'All meals for one day on a single sheet',
    keywords: 'posting post menu board full day all meals',
  },
  'posting-menu-extension-sheet': {
    group: 'meal-service', family: 'Posting Menu',
    description: 'Overflow sheet for long posted menus',
    keywords: 'posting extension overflow continuation sheet',
  },
  'posting-menu-diet-specific': {
    group: 'meal-service', family: 'Posting Menu',
    description: 'Posted menu filtered to one diet type',
    keywords: 'posting diet specific by diet therapeutic',
  },
  'posting-menu-general': {
    group: 'meal-service', family: 'Posting Menu',
    description: 'General posted menu, all diets',
    keywords: 'posting general regular house menu',
  },
  'posting-menu-general-2': {
    group: 'meal-service', family: 'Posting Menu',
    description: 'General posted menu, alternate layout',
    keywords: 'posting general regular house menu second alternate',
  },
  'posting-menu-selective-menu': {
    group: 'meal-service', family: 'Posting Menu',
    description: 'Posted menu showing selective choices',
    keywords: 'posting selective choice menu',
  },
  'posting-menu-specific-resident': {
    group: 'meal-service', family: 'Posting Menu',
    description: 'Posted menu for one resident',
    keywords: 'posting specific resident patient individual single',
  },
  'tray-line-assigned': {
    group: 'meal-service', family: 'Tray Line',
    description: 'Tray line list for residents with assigned seating',
    keywords: 'tray line assigned seating station',
  },
  'tray-line-unassigned': {
    group: 'meal-service', family: 'Tray Line',
    description: 'Tray line list for residents without assigned seating',
    keywords: 'tray line unassigned no seat',
  },
  'tray-line-locator': {
    group: 'meal-service', family: 'Tray Line',
    description: 'Where each resident’s tray goes',
    keywords: 'tray line locator location where room find delivery',
  },
  'tray-line-overrides': {
    group: 'meal-service', family: 'Tray Line',
    description: 'Manual overrides applied on the tray line',
    keywords: 'tray line override manual exception change',
  },
  'tray-line-order': {
    group: 'meal-service', family: 'Tray Line',
    description: 'Order in which trays come off the line',
    keywords: 'tray line order sequence sort run order',
  },
  'diet-order-tray-line': {
    group: 'meal-service', family: 'Tray Line',
    description: 'Diet orders arranged in tray line sequence',
    keywords: 'diet order tray line sequence',
  },
  'meal-feeding-status-assigned': {
    group: 'meal-service', family: 'Feeding Status',
    description: 'Residents with an assigned feeding status',
    keywords: 'feeding status assigned assist help eating',
  },
  'meal-feeding-status-unassigned': {
    group: 'meal-service', family: 'Feeding Status',
    description: 'Residents with no feeding status set',
    keywords: 'feeding status unassigned missing blank assist',
  },
  'meal-portion': {
    group: 'meal-service',
    description: 'Portion sizes by resident and meal',
    keywords: 'portion size serving amount small large double',
  },
  'med-pass-summary': {
    group: 'meal-service', family: 'Med Pass',
    description: 'Med pass supplement summary for the nursing cart',
    keywords: 'med pass medication supplement nursing cart summary',
  },
  'beverage-by-cold-hot': {
    group: 'meal-service', family: 'Beverage',
    description: 'Beverage counts split cold vs hot',
    keywords: 'beverage drink cold hot coffee juice milk tea',
  },
  'beverage-by-meal': {
    group: 'meal-service', family: 'Beverage',
    description: 'Beverage counts per mealtime',
    keywords: 'beverage drink by meal breakfast lunch supper dinner',
  },
  'beverage-by-resident': {
    group: 'meal-service', family: 'Beverage',
    description: 'Beverage preferences per resident',
    keywords: 'beverage drink by resident patient individual',
  },
  'beverage-by-set': {
    group: 'meal-service', family: 'Beverage',
    description: 'Beverage counts grouped by set',
    keywords: 'beverage drink by set group cart',
  },
  'beverage-total': {
    group: 'meal-service', family: 'Beverage',
    description: 'Total beverage counts for production',
    keywords: 'beverage drink total count sum all',
  },
  nourishments: {
    group: 'meal-service',
    description: 'Nourishment labels, list or summary',
    keywords: 'nourishment snack between meal hs bedtime supplement label',
  },
  'resident-labels': {
    group: 'meal-service', family: 'Labels',
    description: 'Printed labels for residents',
    keywords: 'resident patient label sticker name tag',
  },

  // ═══ CLINICAL ════════════════════════════════════════════════════
  'weight-significant-change': {
    group: 'clinical', family: 'Weight',
    description: 'Residents with a significant weight change',
    keywords: 'weight significant change loss gain percent alert flag',
  },
  'weight-trigger': {
    group: 'clinical', family: 'Weight',
    description: 'Weights breaching configured trigger thresholds',
    keywords: 'weight trigger threshold alert flag risk',
  },
  'weight-matrix': {
    group: 'clinical', family: 'Weight',
    description: 'Weight grid across residents and dates',
    keywords: 'weight matrix grid table history compare',
  },
  'weight-entry-worksheet': {
    group: 'clinical', family: 'Weight',
    description: 'Blank worksheet for recording weights',
    keywords: 'weight entry worksheet blank form record capture scale',
  },
  weights: {
    group: 'clinical', family: 'Weight',
    description: 'Recent weekly or monthly weights for each resident, as on Weight Entry',
    keywords: 'weight entry weekly monthly recent scale list print',
  },
  skin: {
    group: 'clinical',
    description: 'Residents with skin conditions affecting nutrition',
    keywords: 'skin wound pressure ulcer sore braden integrity',
  },
  'acceptance-meal': {
    group: 'clinical', family: 'Acceptance',
    description: 'How much of each meal residents ate',
    keywords: 'acceptance meal intake percent eaten consumption refused',
  },
  'acceptance-med-pass': {
    group: 'clinical', family: 'Acceptance',
    description: 'Med pass supplement acceptance',
    keywords: 'acceptance med pass supplement taken refused',
  },
  'acceptance-monthly': {
    group: 'clinical', family: 'Acceptance',
    description: 'Meal acceptance summarised by month',
    keywords: 'acceptance monthly month summary trend',
  },
  'acceptance-nourishments': {
    group: 'clinical', family: 'Acceptance',
    description: 'Nourishment acceptance by resident',
    keywords: 'acceptance nourishment snack taken refused',
  },
  'meal-summary': {
    group: 'clinical',
    description: 'Summary of meals served to a resident',
    keywords: 'meal summary overview recap',
  },
  'meal-dislike': {
    group: 'clinical',
    description: 'Resident food dislikes and refusals',
    keywords: 'dislike refuse hate preference aversion wont eat',
  },
  'nutritional-analysis': {
    group: 'clinical', family: 'Nutrition',
    description: 'Nutrient analysis grid for menus or residents',
    keywords: 'nutrition nutritional analysis nutrient calorie protein sodium dri rda',
  },
  'nutritional-recommendations': {
    group: 'clinical', family: 'Nutrition',
    description: 'Dietitian recommendations by resident',
    keywords: 'nutrition recommendation rd dietitian suggest advice',
  },
  'assessment-template': {
    group: 'clinical', family: 'Assessment',
    description: 'Blank nutritional assessment form',
    keywords: 'assessment template blank form mds nutrition evaluation',
  },
  'resident-assessment-list': {
    group: 'clinical', family: 'Assessment',
    description: 'Assessments due or completed across residents',
    keywords: 'assessment list due overdue schedule tracking',
  },
  'plan-of-care-iadl-adl-sheet': {
    group: 'clinical', family: 'Plan of Care',
    description: 'Monthly grid of each resident’s scheduled daily-living tasks to initial',
    keywords: 'poc care plan iadl adl activities daily living schedule grid initials monthly flowsheet',
  },
  'plan-of-care-quick-print': {
    group: 'clinical', family: 'Plan of Care',
    description: 'Service plan, evaluation and IADL/ADL sheet of each resident in one print',
    keywords: 'poc care plan quick print all together packet bundle',
  },
  'plan-of-care-resident-evaluation': {
    group: 'clinical', family: 'Plan of Care',
    description: 'Evaluation scores and level of care for each resident',
    keywords: 'poc care plan locd level care determination score evaluation assisted living',
  },
  'plan-of-care-resident-service-plan': {
    group: 'clinical', family: 'Plan of Care',
    description: 'Problems, interventions and responsible parties of each resident’s plan',
    keywords: 'poc care plan service problems interventions approaches responsible party signatures',
  },
  'plan-of-care-service-planning-guide': {
    group: 'clinical', family: 'Plan of Care',
    description: 'The problems and interventions set up for each domain',
    keywords: 'poc care plan guide domains problems interventions reference blank configuration',
  },
  'interaction-log': {
    group: 'admin',
    description: 'System-wide log of who changed what, for a date range',
    keywords: 'interaction log audit history activity trail who changed',
  },
  'special-needs': {
    group: 'clinical',
    description: 'Residents with special dietary needs',
    keywords: 'special needs requirement accommodation restriction',
  },
  supplement: {
    group: 'clinical',
    description: 'Supplements ordered by resident',
    keywords: 'supplement shake ensure boost protein drink fortify',
  },
  'tube-feed-labels': {
    group: 'clinical', family: 'Tube Feed',
    description: 'Labels and summary for tube feed bags',
    keywords: 'tube feed enteral label sticker bag summary peg gtube tf formula rate',
  },
  'tube-feed-list': {
    group: 'clinical', family: 'Tube Feed',
    description: 'List of residents on tube feeding',
    keywords: 'tube feed enteral list roster who',
  },
  'tube-feed-products': {
    group: 'clinical', family: 'Tube Feed',
    description: 'Tube feed products in use',
    keywords: 'tube feed enteral product formula brand jevity osmolite',
  },
  'tube-feed-enteral-feeding-label': {
    group: 'clinical', family: 'Tube Feed',
    description: 'Fillable enteral feeding label',
    keywords: 'tube feed enteral label fillable blank',
  },
  'drug-allergies': {
    group: 'clinical', family: 'Allergies',
    description: 'Drug allergies recorded per resident',
    keywords: 'drug allergy medication allergies adverse reaction',
  },
  'food-allergies': {
    group: 'clinical', family: 'Allergies',
    description: 'Food allergies recorded per resident',
    keywords: 'food allergy allergies intolerance anaphylaxis nuts shellfish',
  },
  'allergy-group': {
    group: 'clinical', family: 'Allergies',
    description: 'Allergies rolled up by allergy group',
    keywords: 'allergy group category rollup class',
  },

  // ═══ KITCHEN & PURCHASING ════════════════════════════════════════
  'production-grocery-list': {
    group: 'kitchen', family: 'Purchasing', title: 'Grocery List',
    description: 'What to buy for the upcoming menu',
    keywords: 'grocery shopping list buy order purchase ingredients need production',
  },
  'production-prep-pull': {
    group: 'kitchen', family: 'Purchasing', title: 'Prep / Pull',
    description: 'What to prep and pull from stores',
    keywords: 'prep pull thaw production prepare kitchen tomorrow',
  },
  'production-tally': {
    group: 'kitchen',
    description: 'Production counts by item and meal',
    keywords: 'tally count production how many quantity batch',
  },
  'recipe-book': {
    group: 'kitchen', family: 'Recipes',
    description: 'Full printed recipe book',
    keywords: 'recipe book all recipes cookbook binder',
  },
  'recipe-card': {
    group: 'kitchen', family: 'Recipes',
    description: 'Single recipe card for the line',
    keywords: 'recipe card single one instructions method',
  },
  'recipe-menu-cycle': {
    group: 'kitchen', family: 'Recipes',
    description: 'Recipes used across a menu cycle',
    keywords: 'recipe menu cycle week rotation',
  },
  'recipe-ingredients-menu-cycle': {
    group: 'kitchen', family: 'Recipes',
    description: 'Ingredients needed for a menu cycle',
    keywords: 'recipe ingredient menu cycle quantity needed shopping',
  },
  'menu-cycle-usage': {
    group: 'kitchen',
    description: 'Where each menu cycle is in use',
    keywords: 'menu cycle usage used where rotation assignment',
  },
  'item-data-grid': {
    group: 'kitchen', family: 'Items',
    description: 'Food item master data as a grid',
    keywords: 'item data grid master list food export',
  },
  'item-linking-data-grid': {
    group: 'kitchen', family: 'Items',
    description: 'Items and the items linked to them',
    keywords: 'item linking link linked substitute pair grid table',
  },
  purchasing: {
    group: 'kitchen', family: 'Purchasing',
    description: 'Purchasing activity and totals',
    keywords: 'purchasing purchase buy vendor supplier spend',
  },
  'purchase-order': {
    group: 'kitchen', family: 'Purchasing',
    description: 'Purchase orders raised to vendors',
    keywords: 'purchase order po vendor supplier buy',
  },
  'order-forms': {
    group: 'kitchen', family: 'Purchasing',
    description: 'Blank order forms for vendors',
    keywords: 'order form blank vendor supplier template',
  },

  // ═══ CENSUS & ROSTERS ════════════════════════════════════════════
  'resident-list': {
    group: 'census',
    description: 'Complete resident roster with diet and allergy details',
    keywords: 'resident patient list roster all who census names',
  },
  'resident-data-grid': {
    group: 'census',
    description: 'Residents by unit and room, or sorted by name, as on the Residents grid',
    keywords: 'resident patient data grid table roster room unit floor',
  },
  census: {
    group: 'census',
    description: 'Current facility census',
    keywords: 'census count headcount occupancy how many residents',
  },
  'diet-order': {
    group: 'census', family: 'Diet Order',
    description: 'Current diet orders by resident',
    keywords: 'diet order current active prescription',
  },
  'diet-order-changes': {
    group: 'census', family: 'Diet Order',
    description: 'Diet orders changed in a date range',
    keywords: 'diet order change changed new modified updated',
  },
  'consistency-census': {
    group: 'census', family: 'Diet Order',
    description: 'Census broken down by consistency',
    keywords: 'consistency census texture puree mechanical soft thickened iddsi count',
  },
  'diet-type-census': {
    group: 'census', family: 'Diet Order',
    description: 'Census broken down by diet type',
    keywords: 'diet type census count by diet regular renal diabetic',
  },
  'resident-multiples': {
    group: 'census',
    description: 'Multi-resident cardex or list view',
    keywords: 'multiples cardex list multiple residents card',
  },
  birthday: {
    group: 'census',
    description: 'Resident birthdays in a date range',
    keywords: 'birthday birthdays born dob celebration cake',
  },
  prescriber: {
    group: 'census',
    description: 'Residents grouped by prescriber',
    keywords: 'prescriber doctor physician md provider attending',
  },
  'special-devices': {
    group: 'census',
    description: 'Adaptive equipment assigned to residents',
    keywords: 'special device adaptive equipment utensil plate guard cup assistive',
  },

  // ═══ SELECTIVE MENUS ═════════════════════════════════════════════
  'selective-menu': {
    group: 'selective-menus',
    description: 'Printed selective menus for residents to choose from',
    keywords: 'selective menu choice select pick options',
  },
  'selective-menu-non-returned': {
    group: 'selective-menus', family: 'Selective Menu', title: 'Non-Returned Menus',
    description: 'Residents who have not returned their menu',
    keywords: 'selective menu non returned missing outstanding not back chase',
  },
  'selective-menu-resident-list': {
    group: 'selective-menus', family: 'Selective Menu',
    description: 'Resident list for selective menu distribution',
    keywords: 'selective menu resident patient list distribution',
  },

  // ═══ COST & CREDITS ══════════════════════════════════════════════
  'cost-analysis-menu': {
    group: 'financial', family: 'Cost Analysis',
    description: 'Food cost per menu',
    keywords: 'cost analysis menu food cost ppd per patient day budget spend',
  },
  'cost-analysis-nourishment': {
    group: 'financial', family: 'Cost Analysis',
    description: 'Food cost for nourishments',
    keywords: 'cost analysis nourishment snack cost spend',
  },
  'credits-current-month-summary': {
    group: 'financial', family: 'Credits',
    description: 'Credit activity for the current month',
    keywords: 'credit current month summary balance charge account',
  },
  'credits-end-of-day-balance': {
    group: 'financial', family: 'Credits',
    description: 'Credit balances at end of day',
    keywords: 'credit end of day balance eod close',
  },
  'credits-end-of-month-balance': {
    group: 'financial', family: 'Credits',
    description: 'Credit balances at end of month',
    keywords: 'credit end of month balance eom close statement',
  },
  'credits-item-usage': {
    group: 'financial', family: 'Credits',
    description: 'Items purchased against credits',
    keywords: 'credit item usage purchased bought spent',
  },
  'credits-summary-by-resident': {
    group: 'financial', family: 'Credits',
    description: 'Credit summary per resident',
    keywords: 'credit summary by resident patient account balance',
  },
  'credits-history': {
    group: 'financial', family: 'Credits',
    description: 'Full credit transaction history',
    keywords: 'credit history transaction ledger audit past',
  },
  'management-team-chargeable': {
    group: 'financial', family: 'Management Team',
    description: 'Chargeable items for the management team',
    keywords: 'management team chargeable charge bill guest meal',
  },
  'management-team-special-request': {
    group: 'financial', family: 'Management Team',
    description: 'Special requests tracked for management',
    keywords: 'management team special request extra custom',
  },
  'management-team-fiber': {
    group: 'financial', family: 'Management Team',
    description: 'Fiber supplement tracking',
    keywords: 'management team fiber supplement bulk metamucil',
  },
  'management-team-protein': {
    group: 'financial', family: 'Management Team',
    description: 'Protein supplement tracking',
    keywords: 'management team protein supplement powder',
  },
  'management-team-thickener': {
    group: 'financial', family: 'Management Team',
    description: 'Thickener usage tracking',
    keywords: 'management team thickener thicken dysphagia nectar honey iddsi',
  },

  // ═══ INTERFACES & MENU CONFIG ════════════════════════════════════
  'ehr-diet-order-changes': {
    group: 'interfaces', family: 'EHR',
    description: 'Diet order changes received from the EHR feed',
    keywords: 'ehr emr integration interface diet order change hl7 mirth pcc pointclickcare feed',
  },
  'meal-pattern-notes': {
    group: 'interfaces', family: 'EHR',
    description: 'Meal pattern notes received from the EHR feed',
    keywords: 'ehr emr integration interface meal pattern note hl7 mirth feed',
  },
  analysis: {
    group: 'interfaces',
    description: 'General analysis output',
    keywords: 'analysis analyse report general',
  },

  // ═══ STAFF & ADMIN ═══════════════════════════════════════════════
  'employee-charge-summary': {
    group: 'admin', family: 'Employees',
    description: 'Employee meal charges summarised',
    keywords: 'employee charge summary staff meal bill payroll deduction',
  },
  'employee-production-tally': {
    group: 'admin', family: 'Employees',
    description: 'Production tally for employee meals',
    keywords: 'employee production tally staff meal count',
  },
  'employee-tray-tickets': {
    group: 'admin', family: 'Employees',
    description: 'Tray tickets for employee meals',
    keywords: 'employee tray ticket staff meal card',
  },
  'user-list': {
    group: 'admin',
    description: 'System users and their roles',
    keywords: 'user list login account staff access role security',
  },
  'facility-user-list': {
    group: 'admin',
    description: 'Users with access to this facility',
    keywords: 'facility user list access permission security who can',
  },
  'facility-list': {
    group: 'admin',
    description: 'Facilities configured in the system',
    keywords: 'facility list site building home location all',
  },
};

// ─────────────────────────────────────────────────────────────────
// UNAVAILABLE
// ─────────────────────────────────────────────────────────────────

/**
 * Slugs deliberately kept out of the catalog, with the reason.
 *
 * Verified against `ReportDispatcher.cs` — every entry here either has
 * no registered generator, is registered but commented out, is
 * WinForms-only, is a no-op, is an fk-0 placeholder config, or is an
 * internal report opened from elsewhere in the app rather than browsed.
 *
 * When a generator is re-enabled server-side, delete its line here and
 * add a `REPORT_CATALOG` entry — the coverage test enforces that every
 * config is in exactly one of the two.
 */
export const UNAVAILABLE_REPORTS: Record<string, string> = {
  // Held until dietitian decision 6D (nutrient-specific target rules) is built.
  'nutrition-daily-targets': 'Held until the target rules are approved',
  // No generator registered in ReportDispatcher.cs
  'size-mismatch-list': 'No server-side generator registered',
  // Per-resident AIM change sets (StrDataXML), built by an AIM run. Only the
  // AIM dialogs have one, so they print from there (AIM_DIALOG_REPORTS below);
  // a card on the Reports page would queue an empty report.
  'meal-pattern-aim-changes': 'Printed from the AIM summary after a diet order change (needs that AIM run’s change set, StrDataXML)',
  'aim-selective-menu-changes': 'Printed from the AIM Selective Menu dialog (needs that AIM run’s change set, StrDataXML)',
  // Internal — reached from elsewhere in the app, not browsed
  'tray-ticket-visualizer': 'Opened on demand from Tray Ticket Visualizer',
  'selective-menu-touch': 'Launched from Selective Touch',
  // Per-resident / per-item reports. Each needs the resident, assessment,
  // item or template it describes, and WinForms only prints them from that
  // screen; opened from the catalog they queued with no id and came back
  // empty or failed. The website launches them from the same screens.
  'resident-discharge-summary': 'Printed from the discharge prompt on the Residents screen (ResidentDischarge needs FKey_Patient)',
  'weight-history': 'Printed from the resident Weight History tab',
  'tube-feed-form': 'Printed from the resident Tube Feed tab',
  notes: 'Printed from the resident Notes tab',
  'lab-data': 'Printed from the resident Lab Results tab',
  'problem-goal-approach': 'Printed from the resident Problems tab',
  'intake-study': 'Printed from the resident Intake Study tab',
  'meal-pattern-history': 'Printed from the resident meal pattern Change History dialog',
  'food-group-usage': 'Printed from the Food Group usage dialog (Tables)',
  'global-meal-pattern-usage': 'Printed from the Global Meal Pattern usage dialog',
  'override-menu-list': 'Printed from the Holiday/Override Menu dialog (Menu Cycle)',
  'recipe-list': 'Printed from the Recipes screen',
  'recipe-nutritional-information': 'Printed from the recipe detail screen',
  // Same, but the website has no screen that launches them yet (WinForms:
  // assessment list, item usage window, global adjustment usage window).
  'resident-assessment': 'Needs an assessment; WinForms prints it from the resident assessment list (not on the website yet)',
  'item-usage': 'Needs an item; WinForms prints it from the item usage window (not on the website yet)',
  'global-adjustment-usage': 'Needs a global adjustment; WinForms prints it from its usage window (not on the website yet)',
  // fk-0 placeholder configs with no dispatcher target
  'production-generation': 'Placeholder config (no fKey_RS_Report)',
  beverage: 'Placeholder config (no fKey_RS_Report)',
  'management-team': 'Placeholder config (no fKey_RS_Report)',
};

// ─────────────────────────────────────────────────────────────────
// LOOKUPS
// ─────────────────────────────────────────────────────────────────

/** Fail-open: an unclassified slug resolves to the `unsorted` group. */
export function getCatalogEntry(slug: string): CatalogEntry {
  return (
    REPORT_CATALOG[slug] ?? {
      group: 'unsorted',
      description: '',
    }
  );
}

export function isReportUnavailable(slug: string): boolean {
  return slug in UNAVAILABLE_REPORTS;
}

/**
 * Reports that print only from an AIM dialog, with the message a deep link
 * (/reports/run/<slug>) shows instead of the form. Their generators read the
 * AIM run's change set (StrDataXML), which only the dialog that ran AIM has;
 * queued from a form they would print nothing.
 */
export const AIM_DIALOG_REPORTS: Readonly<Record<string, string>> = {
  'meal-pattern-aim-changes':
    "AIM Meal Pattern Changes prints from the AIM summary, which opens when a {Resident}'s diet order is changed and AIM has changes to suggest. Click Print on that summary.",
  'aim-selective-menu-changes':
    "AIM Selective Menu Changes prints from the AIM Selective Menu dialog, which opens when a {Resident}'s diet order is changed, or from the Auto Generated Selective Menus card on the Dashboard. Click Print in that dialog.",
};

/** The deep-link message for a report that prints only from an AIM dialog. */
export function getAimDialogMessage(slug: string): string | undefined {
  return AIM_DIALOG_REPORTS[slug];
}

/**
 * The "{EHR} Integration" reports. WinForms lists them only when the
 * facility's EHR is active: GetNodeVisibility `lstEHROnly` {92, 93, 108}
 * -> `g_facilitySettings.FacilityEhrActive` (frmDietech_Resident_DataGrid_V2.vb
 * :6544, :6566). Tree 93 prints EhrDietOrderChanges (RS 118), 108 the meal
 * pattern notes (RS 140).
 */
export const EHR_ONLY_REPORTS: ReadonlySet<string> = new Set(['ehr-diet-order-changes', 'meal-pattern-notes']);

/** Hidden for this facility (only when its settings say so; unknown settings show it). */
export function isReportHiddenForFacility(
  slug: string,
  settings: { facilityEhrActive?: boolean | null } | null | undefined,
): boolean {
  return EHR_ONLY_REPORTS.has(slug) && settings?.facilityEhrActive === false;
}

/**
 * The Plan of Care reports. WinForms shows its Reports > Plan of Care
 * menu only to a user whose Plan of Care report level is above 0
 * (frmMain.vb:1220, `g_CU_Dietech_Security.PlanOfCare_ReportLevel > 0`).
 */
export const PLAN_OF_CARE_REPORTS: ReadonlySet<string> = new Set([
  'plan-of-care-iadl-adl-sheet',
  'plan-of-care-quick-print',
  'plan-of-care-resident-evaluation',
  'plan-of-care-resident-service-plan',
  'plan-of-care-service-planning-guide',
]);

/**
 * Hidden for this user. Staged like the desktop's other per-report rules
 * (reportVisibility.ts, docs/roadmap/web-permission-enforcement.md): only on a
 * connection whose permissions are enforced, and only when the role is loaded
 * and says level 0 (an unknown role shows it).
 */
export function isReportHiddenForUser(
  slug: string,
  enforced: boolean,
  role: { dietech_Plan_of_Care_Report_Level?: number | null } | null | undefined,
): boolean {
  if (!enforced || !PLAN_OF_CARE_REPORTS.has(slug)) return false;
  const level = role?.dietech_Plan_of_Care_Report_Level;
  return level != null && level <= 0;
}

export function getUnavailableReason(slug: string): string | undefined {
  return UNAVAILABLE_REPORTS[slug];
}
